import React, { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import metricsApi, { Cluster, CreateClusterRequest, UpdateClusterRequest } from '../../api/metricsApi';

export const ClusterManagement: React.FC = () => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<Cluster | null>(null);
  const [formData, setFormData] = useState<CreateClusterRequest>({
    code: '',
    name: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    try {
      setLoading(true);
      const data = await metricsApi.getClusters();
      setClusters(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching clusters:', err);
      setError('Failed to load clusters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (cluster?: Cluster) => {
    if (cluster) {
      setSelectedCluster(cluster);
      setFormData({
        code: cluster.code,
        name: cluster.name,
        description: cluster.description || '',
      });
    } else {
      setSelectedCluster(null);
      setFormData({
        code: '',
        name: '',
        description: '',
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCluster(null);
    setFormData({
      code: '',
      name: '',
      description: '',
    });
    setFormErrors({});
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.code.trim()) {
      errors.code = 'Cluster code is required';
    } else if (!/^C\d+$/.test(formData.code)) {
      errors.code = 'Cluster code must be in format C1, C2, etc.';
    }

    if (!formData.name.trim()) {
      errors.name = 'Cluster name is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (selectedCluster) {
        await metricsApi.updateCluster(selectedCluster.id, formData as UpdateClusterRequest);
      } else {
        await metricsApi.createCluster(formData);
      }
      await fetchClusters();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving cluster:', err);
      setError('Failed to save cluster. Please try again.');
    }
  };

  const handleOpenDeleteDialog = (cluster: Cluster) => {
    setSelectedCluster(cluster);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedCluster(null);
  };

  const handleDelete = async () => {
    if (!selectedCluster) return;

    try {
      await metricsApi.deleteCluster(selectedCluster.id);
      await fetchClusters();
      handleCloseDeleteDialog();
    } catch (err) {
      console.error('Error deleting cluster:', err);
      setError('Failed to delete cluster. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" 
             style={{ borderColor: theme.colors.primary.main }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2
            className="text-lg font-semibold"
            style={{
              color: theme.colors.text.primary,
              fontFamily: theme.fonts.primary,
            }}
          >
            Cluster Management
          </h2>
          <p
            className="text-sm mt-1"
            style={{ 
              color: theme.colors.text.secondary,
              fontFamily: theme.fonts.primary,
            }}
          >
            Manage impact clusters for organizing metrics
          </p>
        </div>
        <button
          onClick={() => handleOpenDialog()}
          className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 hover:opacity-90"
          style={{
            backgroundColor: theme.colors.primary.main,
            color: 'white',
            fontFamily: theme.fonts.primary,
          }}
        >
          <Plus className="w-4 h-4" />
          <span>New Cluster</span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div 
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: theme.colors.status.error,
            color: theme.colors.status.error,
          }}
        >
          {error}
        </div>
      )}

      {/* Clusters Table */}
      <div className="overflow-hidden rounded-lg shadow-sm border" style={{ borderColor: theme.colors.border.light }}>
        <table className="w-full">
          <thead>
            <tr style={{ backgroundColor: 'rgba(250, 175, 144, 0.08)' }}>
              <th className="text-left px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Code</th>
              <th className="text-left px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Name</th>
              <th className="text-left px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Description</th>
              <th className="text-center px-6 py-4" style={{ 
                color: theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em',
                fontFamily: theme.fonts.primary,
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster) => (
              <tr 
                key={cluster.id}
                className="hover:bg-opacity-30 transition-colors"
                style={{
                  borderBottom: `1px solid ${theme.colors.border.light}`,
                }}
              >
                <td className="px-6 py-4">
                  <span 
                    className="px-2.5 py-1 rounded-md text-xs font-semibold"
                    style={{
                      backgroundColor: theme.colors.primary.main + '30',
                      color: theme.colors.primary.dark,
                      border: `1px solid ${theme.colors.primary.main}50`,
                      fontFamily: theme.fonts.primary,
                    }}
                  >
                    {cluster.code}
                  </span>
                </td>
                <td className="px-6 py-4" style={{ 
                  color: theme.colors.text.primary,
                  fontFamily: theme.fonts.primary,
                  fontSize: '14px',
                  fontWeight: 500,
                }}>
                  {cluster.name}
                </td>
                <td className="px-6 py-4" style={{ 
                  color: theme.colors.text.secondary,
                  fontFamily: theme.fonts.primary,
                  fontSize: '13px',
                }}>
                  {cluster.description || '-'}
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-center items-center gap-2">
                    <button
                      onClick={() => handleOpenDialog(cluster)}
                      className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                      title="Edit cluster"
                      style={{
                        color: theme.colors.text.secondary,
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteDialog(cluster)}
                      className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                      title="Delete cluster"
                      style={{
                        color: theme.colors.status.error,
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div 
              className="px-6 py-4 border-b flex items-center justify-between"
              style={{ borderColor: theme.colors.border.light }}
            >
              <h3 
                className="text-lg font-semibold"
                style={{
                  color: theme.colors.text.primary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                {selectedCluster ? 'Edit Cluster' : 'Create New Cluster'}
              </h3>
              <button
                onClick={handleCloseDialog}
                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" style={{ color: theme.colors.text.secondary }} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Code Field */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{
                    color: theme.colors.text.primary,
                    fontFamily: theme.fonts.primary,
                  }}
                >
                  Cluster Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={!!selectedCluster}
                  placeholder="e.g., C1"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 disabled:bg-gray-100"
                  style={{
                    borderColor: formErrors.code ? theme.colors.status.error : theme.colors.border.light,
                    fontFamily: theme.fonts.primary,
                    fontSize: '14px',
                  }}
                />
                {formErrors.code && (
                  <p className="mt-1 text-xs" style={{ color: theme.colors.status.error }}>
                    {formErrors.code}
                  </p>
                )}
              </div>

              {/* Name Field */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{
                    color: theme.colors.text.primary,
                    fontFamily: theme.fonts.primary,
                  }}
                >
                  Cluster Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter cluster name"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                  style={{
                    borderColor: formErrors.name ? theme.colors.status.error : theme.colors.border.light,
                    fontFamily: theme.fonts.primary,
                    fontSize: '14px',
                  }}
                />
                {formErrors.name && (
                  <p className="mt-1 text-xs" style={{ color: theme.colors.status.error }}>
                    {formErrors.name}
                  </p>
                )}
              </div>

              {/* Description Field */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{
                    color: theme.colors.text.primary,
                    fontFamily: theme.fonts.primary,
                  }}
                >
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter cluster description"
                  rows={3}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 resize-none"
                  style={{
                    borderColor: theme.colors.border.light,
                    fontFamily: theme.fonts.primary,
                    fontSize: '14px',
                  }}
                />
              </div>
            </div>

            <div 
              className="px-6 py-4 border-t flex justify-end gap-3"
              style={{ borderColor: theme.colors.border.light }}
            >
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 rounded-md border transition-colors hover:bg-gray-50"
                style={{
                  borderColor: theme.colors.border.light,
                  color: theme.colors.text.secondary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-md transition-colors hover:opacity-90"
                style={{
                  backgroundColor: theme.colors.primary.main,
                  color: 'white',
                  fontFamily: theme.fonts.primary,
                }}
              >
                {selectedCluster ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && selectedCluster && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div 
              className="px-6 py-4 border-b"
              style={{ borderColor: theme.colors.border.light }}
            >
              <h3 
                className="text-lg font-semibold"
                style={{
                  color: theme.colors.text.primary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                Confirm Delete
              </h3>
            </div>

            <div className="px-6 py-4">
              <p 
                style={{
                  color: theme.colors.text.primary,
                  fontFamily: theme.fonts.primary,
                  fontSize: '14px',
                }}
              >
                Are you sure you want to delete cluster <strong>{selectedCluster.code} - {selectedCluster.name}</strong>?
              </p>
              <p 
                className="mt-2"
                style={{
                  color: theme.colors.status.error,
                  fontFamily: theme.fonts.primary,
                  fontSize: '13px',
                }}
              >
                Warning: This action cannot be undone. All metrics associated with this cluster will need to be reassigned.
              </p>
            </div>

            <div 
              className="px-6 py-4 border-t flex justify-end gap-3"
              style={{ borderColor: theme.colors.border.light }}
            >
              <button
                onClick={handleCloseDeleteDialog}
                className="px-4 py-2 rounded-md border transition-colors hover:bg-gray-50"
                style={{
                  borderColor: theme.colors.border.light,
                  color: theme.colors.text.secondary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md transition-colors hover:opacity-90"
                style={{
                  backgroundColor: theme.colors.status.error,
                  color: 'white',
                  fontFamily: theme.fonts.primary,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};